/**
 * Step 11 — the whole brand in one column, with a way to change every line of
 * it (spec §2.2, `review`).
 *
 * Two jobs. The SUMMARY is a read-back: every answer the walk collected, in
 * the walk's own words, each with a **Change** that jumps to its step and
 * comes straight back here (`returnTo: "review"`). And **Adjust on the video**
 * swaps this column for the Focus editor — the operator's "editor at the end,
 * tools around the preview" — so the fine things (a slider, a colour, the
 * exact distance) are one click away without ever having been a step.
 *
 * "Create my brand" is the only primary: the channel is created HERE, and the
 * Done screen that follows is a landing, not another form. A name that is
 * already taken sends the operator back to `basics` with the sentence at the
 * input, which is the one failure that is not fixable on this screen (§8).
 */
import { useState, type ReactNode } from "react";
import { useMinWidth } from "../../../lib/useIsMobile";
import { ArrowLeft, ArrowRight, Loader2, Pencil, SlidersHorizontal } from "lucide-react";
import {
  cardPctToCorner,
  nearestSizePreset,
  type SizeName,
} from "../../../lib/brandSetup/geometry";
import { LAYOUT_DEFAULTS, buildBaseLayout, effectiveSlug, slugify } from "../../../lib/brandSetup/payload";
import { effectiveDesign, effectiveFontNames, type BrandSetupState } from "../../../lib/brandSetup/state";
import { STUDIED_STEPS, stepForChipField, type StepId } from "../../../lib/brandSetup/steps";
import { MONO_LABEL, primaryButton, secondaryButton } from "../ui";

const SIZE_WORD: Record<SizeName, string> = { small: "Small", medium: "Medium", large: "Large" };
const CORNER_WORD = { tl: "Top left", tr: "Top right", bl: "Bottom left", br: "Bottom right" } as const;

export function ReviewStep(props: {
  state: BrandSetupState;
  /** Jump to a step and come back here. */
  onChange: (id: StepId) => void;
  onCreate: () => void;
  /** The channel this walk already created — Create must not fire twice. */
  createdSlug: string | null;
  /** Edit the address by hand (the slug is immutable once created). */
  onSlug: (value: string) => void;
  submitting: boolean;
  error: string | null;
  /** The Focus editor, mounted by the shell (it owns the sample props). */
  adjusting: boolean;
  onAdjust: (v: boolean) => void;
  studio: ReactNode;
  onBack: () => void;
}): React.JSX.Element {
  const { state, onChange, createdSlug, submitting, error, adjusting, onAdjust, studio } = props;
  const [editingSlug, setEditingSlug] = useState(false);
  const wide = useMinWidth(900);

  if (adjusting) {
    return (
      <div style={{ display: "grid", gap: 12, minWidth: 0 }}>
        {studio}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" onClick={() => onAdjust(false)} style={primaryButton()}>
            Done adjusting
          </button>
          <span style={{ fontSize: 12.5, color: "var(--ql-fg-subtle)" }}>
            Everything here is saved with your brand.
          </span>
        </div>
      </div>
    );
  }

  const base = buildBaseLayout(state);
  const design = effectiveDesign(state)?.layout;
  const cap = { ...base.caption, ...design?.caption };
  const direction = state.language.direction === "rtl" ? "rtl" : "ltr";
  const sizeWord = SIZE_WORD[nearestSizePreset(direction, cap.fontSizePx ?? 165)];
  const positionWord = cap.justify === "flex-end" ? "Lower third" : "Middle";

  const movedCard = design?.speakerCard;
  const corner = CORNER_WORD[
    cardPctToCorner({
      xPct: movedCard?.xPct ?? base.speakerCard?.xPct ?? LAYOUT_DEFAULTS.speakerCardXPct,
      yPct: movedCard?.yPct ?? base.speakerCard?.yPct ?? LAYOUT_DEFAULTS.speakerCardYPct,
    })
  ];

  const fonts = effectiveFontNames(state);
  // EVERY line on the video, not just the first: the text step edits the
  // whole list now (round-2 §2.4), so the read-back lists the whole list.
  const textOnVideo = (design?.overlays ?? []).map((o) => o.text.trim()).filter(Boolean).join(" · ");
  // What the study applied that no step ever showed — darkening, line height,
  // pacing. Counted, never listed: the place to see them is the editor.
  const extras = state.chips.filter((c) => {
    if (state.accepted[c.field] === false) return false;
    const step = stepForChipField(c.field);
    return !step || !STUDIED_STEPS.has(step);
  }).length;

  const actions = (
    <div style={{ display: "grid", gap: 10, justifyItems: wide ? "start" : "stretch" }}>
      <div
        style={{
          display: "flex",
          gap: wide ? 12 : 10,
          flexWrap: "wrap",
          alignItems: "center",
          flexDirection: wide ? "row" : "column-reverse",
        }}
      >
        <button
          type="button"
          onClick={props.onCreate}
          disabled={submitting || !!createdSlug}
          style={{
            ...primaryButton(submitting || !!createdSlug),
            ...(wide ? {} : { width: "100%", justifyContent: "center" }),
          }}
        >
          {submitting ? <Loader2 size={16} className="ql-spin" /> : <ArrowRight size={16} />}
          Create my brand
        </button>
        <div style={{ display: "flex", gap: 10, ...(wide ? {} : { width: "100%" }) }}>
          <button
            type="button"
            onClick={() => onAdjust(true)}
            style={{ ...secondaryButton(), ...(wide ? {} : { flex: 1, justifyContent: "center", padding: "12px 14px" }) }}
          >
            <SlidersHorizontal size={15} />
            Adjust on the video
          </button>
          <button
            type="button"
            onClick={props.onBack}
            style={{ ...secondaryButton(), ...(wide ? {} : { justifyContent: "center", padding: "12px 18px" }) }}
          >
            <ArrowLeft size={15} />
            Back
          </button>
        </div>
      </div>
      {/* Created once, created for good: the rail can walk back here after
          the POST landed, and a second click would only 409. */}
      {createdSlug && !error && (
        <div style={{ fontSize: 13.5, color: "var(--ql-fg-muted)", lineHeight: 1.6 }}>
          Already created — {createdSlug} is ready.
        </div>
      )}
      {error && <div style={{ fontSize: 13.5, color: "var(--ql-danger)", lineHeight: 1.6, maxWidth: 460 }}>{error}</div>}
    </div>
  );

  const column = (
    <div style={{ display: "grid", gap: 20, minWidth: 0, paddingBottom: wide ? 0 : 128 }}>
      <div style={{ display: "grid" }}>
        <Row label="Name" onChange={() => onChange("basics")}>
          {state.brandName.trim() || "—"}
        </Row>
        {/* The address is fixed once the channel exists (§2.2 done): before
            that it is the one field with no step of its own. */}
        <Row
          label="Address"
          hint="The short name used in links and files."
          onChange={createdSlug ? undefined : () => setEditingSlug((v) => !v)}
          changeLabel={editingSlug ? "Done" : "Change"}
        >
          {editingSlug && !createdSlug ? (
            <input
              autoFocus
              value={effectiveSlug(state)}
              onChange={(e) => props.onSlug(slugify(e.target.value))}
              aria-label="Channel address"
              spellCheck={false}
              style={{
                width: "100%",
                padding: "6px 10px",
                borderRadius: 9,
                border: "1px solid rgba(255,255,255,.14)",
                background: "rgba(17,24,39,.5)",
                color: "var(--ql-fg)",
                fontFamily: "var(--ql-font-mono)",
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          ) : (
            <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 13, color: "var(--ql-fg-muted)" }}>
              {effectiveSlug(state) || "—"}
            </span>
          )}
        </Row>
        <Row label="Language" onChange={() => onChange("basics")}>
          {state.language.displayName}
        </Row>
        {/* Thumb OR words, never both (spec §2.2): an uploaded logo IS its own
            label, and the drawn wordmark says where it came from. Showing the
            picture and the sentence together is what ran the row out of width
            on a phone. */}
        <Row label="Logo" onChange={() => onChange("logo")}>
          {state.logoKind === "upload" && state.logoDataUrl ? (
            <img
              src={state.logoDataUrl}
              alt="Your logo"
              style={{ height: 24, maxWidth: "100%", objectFit: "contain", objectPosition: "left", borderRadius: 4 }}
            />
          ) : state.logoKind === "wordmark" ? (
            "Wordmark from your name"
          ) : (
            // Never "—": with nothing uploaded and no wordmark drawn (yet),
            // the phone is showing the placeholder, and the row says so.
            "Placeholder — add your logo"
          )}
        </Row>
        <Row label="Colors" onChange={() => onChange("colors")}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Swatch color={state.colors.ink} title={`Text ${state.colors.ink}`} />
            <Swatch color={state.colors.accent} title={`Highlight ${state.colors.accent}`} />
          </span>
        </Row>
        <Row label="Font" onChange={() => onChange("font")}>
          {fonts.body}
        </Row>
        <Row label="Captions" onChange={() => onChange("captionSize")}>
          {sizeWord} · {positionWord}
        </Row>
        <Row label="Speaker's name" onChange={() => onChange("speaker")}>
          {corner} · {state.displaySame ? "same face as your captions" : fonts.display}
        </Row>
        <Row label="Text on video" onChange={() => onChange("signoff")}>
          {textOnVideo || "None"}
        </Row>
        {extras > 0 && (
          <Row label="From your reel" onChange={() => onAdjust(true)}>
            …and {extras} more we applied
          </Row>
        )}
        {state.designSteps > 0 && (
          <Row label="Adjustments" onChange={() => onAdjust(true)}>
            {state.designSteps} change{state.designSteps === 1 ? "" : "s"} on the video
          </Row>
        )}
      </div>

      {wide ? actions : null}
    </div>
  );

  /* Below 900 px the primary lives in the same fixed safe-area bar every
     other step has (spec §2.1). Review used to be the one screen without
     one, so "Create my brand" sat nine rows below the fold — the last click
     of the whole walk, hidden (QA 2026-09-16, M6). */
  return wide ? column : (
    <>
      {column}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          padding: "12px 16px",
          paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
          background: "rgba(10,10,11,.94)",
          backdropFilter: "blur(12px)",
          borderTop: "1px solid rgba(255,255,255,.07)",
        }}
      >
        {actions}
      </div>
    </>
  );
}



function Swatch({ color, title }: { color: string; title: string }): React.JSX.Element {
  return (
    <span
      title={title}
      style={{ width: 20, height: 20, borderRadius: 6, background: color, border: "1px solid rgba(255,255,255,.18)" }}
    />
  );
}

function Row({ label, children, hint, onChange, changeLabel = "Change" }: {
  label: string;
  children: ReactNode;
  /** One plain sentence under the value, for a row whose LABEL is not
   *  self-explanatory (spec §1.6: no word the operator has to guess at). */
  hint?: string;
  /** Absent → the row is a fact, not a choice (the address after create). */
  onChange?: () => void;
  changeLabel?: string;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 0",
        borderBottom: "1px solid rgba(255,255,255,.06)",
        minWidth: 0,
      }}
    >
      <span style={{ ...MONO_LABEL, width: 112, flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, display: "grid", gap: 2 }}>
        <span
          style={{
            fontSize: 14,
            color: "var(--ql-fg)",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {children}
        </span>
        {hint && (
          <span style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", lineHeight: 1.45 }}>{hint}</span>
        )}
      </span>
      {onChange && (
      <button
        type="button"
        onClick={onChange}
        aria-label={`${changeLabel} ${label}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "5px 11px",
          borderRadius: 9,
          border: "1px solid rgba(255,255,255,.10)",
          background: "transparent",
          color: "var(--ql-fg-muted)",
          fontSize: 12,
          fontFamily: "inherit",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <Pencil size={11} />
        {changeLabel}
      </button>
      )}
    </div>
  );
}
